import {CloseOutlined, InboxOutlined} from '@ant-design/icons';
import {Avatar} from 'antd';
import classNames from 'classnames';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import React, {Fragment, memo, useMemo} from 'react';

import {ReactComponent as IconSvg} from '@/assets/images/warning.svg';
import {Tooltip} from '@/components/Tooltip';
import {getFromSource} from '@/utils/getImages';

const RenderEntityPreview = ({
  record,
  className,
  titleKey,
  subTitleKey,
  textClassName,
  lowercase,
  logoKey,
  subTitle,
  showAvatar,
  truncate,
  transformTitle,
  boldText,
  onRemove,
  customRender,
  actionsRender,
  hideHelp,
  extraDetails,
  isDisabled,
  ...rest
}) => {
  const _nonActive = isDisabled?.(record?.id, rest?.extra) || isDisabled || false;
  const subTitleValue = useMemo(() => get(record, subTitleKey), [record, subTitleKey]);
  const otherCmp = useMemo(() => customRender && customRender(record), [customRender, record]);
  
  const disabled = _nonActive?.disabled || false;
  
  const actionCmp = useMemo(() => actionsRender && actionsRender(record, rest?.extra, disabled), [actionsRender, disabled, record, rest?.extra]);
  
  return (
    <div className={classNames('w-full flex flex-col', {'truncate': truncate})}>
      <div className={classNames(className, 'flex flex-row items-center', {'capitalize': lowercase})}>
        {extraDetails && extraDetails?.({...record, ..._nonActive?.values})}
        {showAvatar && <div className="mr-2 flex self-start">
          <Avatar src={getFromSource(record[logoKey])} icon={<InboxOutlined/>} shape="square" size={'small'} {...rest}/>
        </div>}
        <div
          title={lowercase ? transformTitle(record[titleKey]?.toLowerCase(), record, disabled) : transformTitle(record[titleKey], record, disabled)}
          className={
            classNames(
              textClassName, 'flex-1 mr-2',
              {'truncate': truncate},
              {'line-through italic opacity-25': disabled},
              {'font-semibold': boldText}
            )
          }
        >
          {lowercase ? transformTitle(record[titleKey]?.toLowerCase(), record, disabled) : transformTitle(record[titleKey], record, disabled)}
        </div>
        <div className="flex items-center gap-2">
          {actionCmp}
          <div className="min-w-5 flex items-center">
            {(!hideHelp && !!subTitleValue) &&
              <Tooltip title={subTitleValue} placement="left" className="cursor-pointer">
                <IconSvg style={{height: 15}} className="flex items-center"/>
              </Tooltip>}
            {onRemove && <div onClick={onRemove} className="cursor-pointer ml-2">
              <CloseOutlined/>
            </div>}
          </div>
        </div>
      </div>
      {subTitle}
      {!!otherCmp && <div className="md:pb-2">
        {otherCmp}
      </div>}
    </div>
  );
};

const EntityFromServer = ({keyValue, id, useHook, fallbackRender, ...rest}) => {
  const {isLoading, data} = useHook({[keyValue]: id}, {refetchOnWindowFocus: false});
  if (!data || isLoading) return fallbackRender || <Fragment/>;
  
  return <RenderEntityPreview record={data.data} {...rest}/>;
};

const EntityPreview = ({value, keyValue, useHook, defaultValue, ...rest}) => {
  if (!value) return defaultValue || <Fragment/>;
  if (typeof value === 'string') return <EntityFromServer id={value} keyValue={keyValue} useHook={useHook} {...rest}/>;
  return <RenderEntityPreview record={value} {...rest}/>;
};

export default memo(EntityPreview);

EntityPreview.propTypes = {
  useHook: PropTypes.func,
  lowercase: PropTypes.bool,
  truncate: PropTypes.bool,
  subTitleKey: PropTypes.string,
  defaultValue: PropTypes.any,
  showAvatar: PropTypes.bool,
  isDisabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  boldText: PropTypes.bool,
  hideHelp: PropTypes.bool,
  className: PropTypes.string,
  keyValue: PropTypes.string,
  subTitle: PropTypes.any,
  transformTitle: PropTypes.func,
  onRemove: PropTypes.func,
  customRender: PropTypes.func,
  actionsRender: PropTypes.func,
  textClassName: PropTypes.string,
  value: PropTypes.any,
  extraDetails: PropTypes.any
};

EntityPreview.defaultProps = {
  keyValue: 'id',
};

EntityFromServer.propTypes = {
  id: PropTypes.string,
  fallbackRender: PropTypes.any,
  keyValue: PropTypes.string,
  useHook: PropTypes.func
};

RenderEntityPreview.propTypes = {
  lowercase: PropTypes.bool,
  truncate: PropTypes.bool,
  hideHelp: PropTypes.bool,
  subTitleKey: PropTypes.string,
  transformTitle: PropTypes.func,
  onRemove: PropTypes.func,
  showAvatar: PropTypes.bool,
  isDisabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  boldText: PropTypes.bool,
  titleKey: PropTypes.string,
  logoKey: PropTypes.string,
  subTitle: PropTypes.any,
  className: PropTypes.string,
  textClassName: PropTypes.string,
  customRender: PropTypes.func,
  actionsRender: PropTypes.func,
  extraDetails: PropTypes.func,
  record: PropTypes.any
};

RenderEntityPreview.defaultProps = {
  lowercase: false,
  logoKey: 'logo',
  transformTitle: (e) => e,
  subTitleKey: 'summary',
  titleKey: 'name'
};
